import { Button, Eyebrow, GoldDivider } from '@tt/design-system';
import { useMemo, useState } from 'react';
import { MILES_COST } from '../../../lib/env';
import {
  formatMiles,
  MILES_PROGRAMS,
  type MilesProgramId,
} from '../../../utils/payments/mockMiles';
import { wait } from '../../../utils/wait';
import type { PaymentMethodProps } from '../types';
import { ProgramSelector } from './ProgramSelector';

export function MilesMethod({ onConfirm, isConfirming = false }: PaymentMethodProps) {
  const [programId, setProgramId] = useState<MilesProgramId>('smiles');
  const [redeeming, setRedeeming] = useState(false);

  const program = useMemo(
    () => MILES_PROGRAMS.find((p) => p.id === programId) ?? MILES_PROGRAMS[0],
    [programId],
  );
  const cost = MILES_COST;
  const remaining = Math.max(0, program.balance - cost);
  const insufficient = program.balance < cost;

  async function handleRedeem() {
    if (insufficient || isConfirming || redeeming) return;
    setRedeeming(true);
    await wait(1000);
    setRedeeming(false);
    onConfirm({
      method: 'miles',
      detailsFake: `${program.name} · ${formatMiles(cost)} milhas`,
    });
  }

  return (
    <div className="flex flex-col gap-6">
      <Eyebrow tone="gold">Resgate com milhas</Eyebrow>

      <ProgramSelector value={programId} onChange={setProgramId} />

      <div className="flex flex-col gap-3 rounded-md border border-gold/20 bg-night/40 p-6">
        <div className="flex items-baseline justify-between">
          <span className="font-mono text-xs uppercase tracking-stamp text-champagne">
            Programa
          </span>
          <span className="font-display text-lg text-parchment">{program.name}</span>
        </div>
        <div className="flex items-baseline justify-between">
          <span className="font-mono text-xs uppercase tracking-stamp text-champagne">
            Você possui
          </span>
          <span className="font-display text-lg text-parchment">
            {formatMiles(program.balance)} milhas
          </span>
        </div>
        <div className="flex items-baseline justify-between">
          <span className="font-mono text-xs uppercase tracking-stamp text-champagne">
            Custo
          </span>
          <span className="font-display text-lg text-gold-shimmer">
            −{formatMiles(cost)} milhas
          </span>
        </div>
        <GoldDivider variant="line" />
        <div className="flex items-baseline justify-between">
          <span className="font-mono text-xs uppercase tracking-stamp text-champagne">
            Restantes após resgate
          </span>
          <span className="font-display text-lg text-champagne">
            {formatMiles(remaining)} milhas
          </span>
        </div>
      </div>

      {insufficient ? (
        <p
          role="alert"
          className="rounded-md border border-error/40 bg-error/10 p-3 text-sm text-error"
        >
          Saldo insuficiente em {program.name}. Tente outro programa.
        </p>
      ) : null}

      <div className="border-t border-gold/15 pt-6">
        <Button
          type="button"
          variant="primary"
          size="lg"
          fullWidth
          onClick={handleRedeem}
          disabled={insufficient || isConfirming || redeeming}
        >
          {isConfirming
            ? 'Eternizando seu registro…'
            : redeeming
              ? 'Resgatando milhas…'
              : 'Resgatar milhas'}
        </Button>
        <p className="mt-3 text-center font-mono text-[10px] uppercase tracking-stamp text-champagne/60">
          Modo demonstração · cotação simulada · 1 milha ≈ R$ 0,025
        </p>
      </div>
    </div>
  );
}
